import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Linkedin, Mail, MapPin, Phone } from 'lucide-react'
import { Logo } from '@/components/generals/logo'
import { company } from '@/constants/company'
import { siteConfig } from '@/constants/site'

import {
    FOOTER_COMPANY_LINKS,
    FOOTER_SERVICE_LINKS,
    LEGAL_LINKS
} from '@/constants/navigation'

const linkClass = 'transition-colors hover:text-foreground'

export const Footer: FC = (): ReactNode => {
    return (
        <footer className='border-t border-border bg-card'>
            <div className='mx-auto grid max-w-6xl gap-12 px-6 py-16 md:grid-cols-12 md:gap-8'>
                <div className='md:col-span-5'>
                    <Link href='/' aria-label='ConcreTrack, zur Startseite' className='inline-flex'>
                        <Logo />
                    </Link>

                    <p className='mt-5 max-w-sm text-[0.95rem] leading-relaxed text-muted-foreground'>
                        Betontemperatur-Monitoring für Baustellen in Deutschland, Österreich und der Schweiz. Ein Produkt der BBA GmbH aus Bad Saarow.
                    </p>

                    <p className='mt-5 font-mono text-[0.72rem] font-medium uppercase tracking-[0.2em] text-primary'>
                        {company.tagline}
                    </p>
                </div>

                <nav aria-label='Fußzeile' className='grid grid-cols-2 gap-8 md:col-span-4'>
                    <div>
                        <p className='text-sm font-semibold text-foreground'>
                            Leistung
                        </p>

                        <ul className='mt-4 grid gap-2.5 text-[0.95rem] text-muted-foreground'>
                            {FOOTER_SERVICE_LINKS.map((link) => (
                                <li key={link.href}>
                                    <Link href={link.href} className={linkClass}>
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <p className='text-sm font-semibold text-foreground'>
                            Unternehmen
                        </p>

                        <ul className='mt-4 grid gap-2.5 text-[0.95rem] text-muted-foreground'>
                            {FOOTER_COMPANY_LINKS.map((link) => (
                                <li key={link.href}>
                                    <Link href={link.href} className={linkClass}>
                                        {link.label}
                                    </Link>
                                </li>
                            ))}

                            <li>
                                <a href={siteConfig.loginUrl} className={linkClass}>
                                    Kunden-Login
                                </a>
                            </li>
                        </ul>
                    </div>
                </nav>

                <div className='md:col-span-3'>
                    <p className='text-sm font-semibold text-foreground'>
                        Kontakt
                    </p>

                    <ul className='mt-4 grid gap-3 text-[0.95rem] text-muted-foreground'>
                        <li>
                            <a href={company.phoneHref} className='inline-flex items-center gap-2.5 transition-colors hover:text-foreground'>
                                <Phone className='size-4 shrink-0 text-primary' aria-hidden='true' />
                                {company.phone}
                            </a>
                        </li>

                        <li>
                            <a href={`mailto:${company.email}`} className='inline-flex items-center gap-2.5 break-all transition-colors hover:text-foreground'>
                                <Mail className='size-4 shrink-0 text-primary' aria-hidden='true' />
                                {company.email}
                            </a>
                        </li>

                        <li className='flex gap-2.5'>
                            <MapPin className='mt-0.5 size-4 shrink-0 text-primary' aria-hidden='true' />

                            <address className='not-italic leading-relaxed'>
                                {company.legalName}
                                <br />
                                {company.street}
                                <br />
                                {company.postalCode} {company.city}
                            </address>
                        </li>

                        {siteConfig.links?.linkedin && (
                            <li>
                                <a
                                    href={siteConfig.links.linkedin}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='inline-flex items-center gap-2.5 transition-colors hover:text-foreground'
                                >
                                    <Linkedin className='size-4 shrink-0 text-primary' aria-hidden='true' />
                                    ConcreTrack auf LinkedIn
                                </a>
                            </li>
                        )}
                    </ul>
                </div>
            </div>

            <div className='border-t border-border'>
                <div className='mx-auto flex max-w-6xl flex-col gap-4 px-6 py-6 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        © <span suppressHydrationWarning>{new Date().getFullYear()}</span> {company.legalName}. ConcreTrack ist eine Marke der {company.legalName}.
                    </p>

                    <ul className='flex flex-wrap gap-x-6 gap-y-2'>
                        {LEGAL_LINKS.map((link) => (
                            <li key={link.href}>
                                <Link href={link.href} className={linkClass}>
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </footer>
    )
}
